import type { PetWithStatus, TickOutcome, ActionOutcome } from '@shared/domain/petModels';
import type { PaymentChain } from '@shared/domain/payments/paymentTypes';
import type {
  MintPetResponse,
  FetchOwnerPetsResponse,
  RequestEvolutionTokenResult,
  SyncPetStateResult,
  SpeciesSupplyMetricDto,
  FetchSpeciesSupplyResponse,
  PetBrowseSummary,
  BrowsePetsFilters,
  BrowsePetsRequest,
  BrowsePetsResponse,
  BrowsePetsPageResult,
  CommunityStatsDto,
  FetchCommunityStatsResponse,
  TrainerRankingSummary,
  BrowseTrainersRequest,
  BrowseTrainersResponse,
  BrowseTrainersPageResult,
  TrainerProfile,
  TrainerProfileResponse,
  SaveTrainerProfileRequest,
  TransferSuikaResponse,
  MyTransactionDto,
  MyTransactionsResponse,
} from '@shared/api/contracts';
import { API_BASE_URL } from '../config/apiConfig';
import { loadAdminToken } from '../utils/adminAuth';
import { getSession } from '../auth/sessionStore';

/**
 * API Configuration Constants
 */
export const API_VERSION = 'v1'; // Exported for testing/configuration
const API_HEALTH_PATH = '/health';
const API_BASE_PATH = '/api';
const JSON_HEADERS = { 'Content-Type': 'application/json' } as const;

/** Authorization header for the logged-in user's session JWT, when present. */
function sessionAuthHeader(): Record<string, string> {
  const token = getSession()?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function adminSessionHeaders(): Record<string, string> {
  const token = loadAdminToken();
  return token
    ? {
        ...JSON_HEADERS,
        Authorization: `Bearer ${token}`,
      }
    : { ...JSON_HEADERS };
}

// Re-export types for convenience
export type {
  MintPetResponse,
  RequestEvolutionTokenResult,
  SyncPetStateResult,
  SpeciesSupplyMetricDto,
  PetBrowseSummary,
  BrowsePetsFilters,
  BrowsePetsRequest,
  BrowsePetsResponse,
  BrowsePetsPageResult,
  CommunityStatsDto,
  TrainerRankingSummary,
  BrowseTrainersRequest,
  BrowseTrainersResponse,
  BrowseTrainersPageResult,
  TrainerProfile,
  SaveTrainerProfileRequest,
  MyTransactionDto,
};

/** Error thrown by {@link parseJson} on a non-ok response, carrying the parsed body so
 * callers can inspect extra fields (e.g. `nameTaken`) beyond the plain error message. */
export class BackendRequestError extends Error {
  constructor(message: string, public readonly data: unknown) {
    super(message);
    this.name = 'BackendRequestError';
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json();

  if (!response.ok) {
    const maybeObject = data as { error?: string } | null | undefined;
    const message = typeof maybeObject === 'object' ? maybeObject?.error : undefined;
    throw new BackendRequestError(message || response.statusText || 'Backend request failed', data);
  }

  return data as T;
}

function withBase(path: string): string {
  // Health check and other non-versioned endpoints
  if (path === API_HEALTH_PATH) {
    return `${API_BASE_URL}${path}`;
  }
  // Versioned API endpoints
  return `${API_BASE_URL}${API_BASE_PATH}/${API_VERSION}${path}`;
}

/**
 * Mint a new pet NFT via the backend.
 * Backend automatically derives rarity, description, and imageUrl from species configuration.
 *
 * @param species - Species ID (0=Bireon, 1=Aqua, etc.)
 * @param name - Custom pet name chosen by user
 * @param userAddress - Wallet address to receive the NFT
 * @param amountUsdc - Payment amount in USDC (must match species rarity requirement)
 * @param txDigest - (Optional) Transaction digest of payment to backend wallet (required for paid adoptions)
 * @param referralAddress - (Optional) Referral wallet address for commission split, or "SUIKAMON" for PVP discount
 * @param pvpSession - (Optional) PVP session ID when using PVP discount (referralAddress must be "SUIKAMON")
 * @param chain - Payment chain the txDigest belongs to (default 'sui')
 * @param promotePetId - (Optional, Task 14) Convert-to-own: promote this existing free pet
 *   into custody instead of minting a brand-new one. Progress carries over.
 * @param newName - (Optional, Task 14) Replacement name to set before promoting, used when
 *   the free pet's original name was taken by someone else while it was played off-chain.
 */
export async function mintPet(
  species: number,
  name: string,
  userAddress: string,
  amountUsdc: number,
  txDigest?: string,
  referralAddress?: string,
  pvpSession?: string,
  chain: PaymentChain = 'sui',
  promotePetId?: string,
  newName?: string,
): Promise<MintPetResponse> {
  const body: Record<string, unknown> = {
    species,
    name,
    userAddress,
    amountSUI: amountUsdc, // wire field name unchanged; value is now USDC-denominated
    txDigest,
    chain, // payment chain the txDigest belongs to (default 'sui')
  };

  // Only include referralAddress if provided
  if (referralAddress) {
    body.referralAddress = referralAddress;
  }

  // Only include pvpSession if provided (for PVP discount validation)
  if (pvpSession) {
    body.pvpSession = pvpSession;
  }

  // Only include promote fields when converting a free pet to owned (Task 14)
  if (promotePetId) {
    body.promotePetId = promotePetId;
  }
  if (newName) {
    body.newName = newName;
  }

  const response = await fetch(withBase('/payment/mint-pet'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });

  return parseJson<MintPetResponse>(response);
}

/**
 * @param ownerAddress - wallet to list pets for.
 * @param options.asSelf - default true. When true, attaches the caller's OWN session so the
 *   backend can resolve by account_id (works across the caller's linked wallets). Callers
 *   viewing a DIFFERENT trainer's public collection (e.g. TrainerProfilePage) must pass
 *   `{ asSelf: false }` — otherwise an authenticated caller's session silently overrides
 *   `ownerAddress` server-side and returns the caller's OWN pets instead of the requested
 *   trainer's.
 */
export async function fetchOwnerPets(
  ownerAddress: string,
  options?: { asSelf?: boolean },
): Promise<PetWithStatus[]> {
  const url = new URL(withBase('/pets'));
  url.searchParams.set('owner', ownerAddress);
  const asSelf = options?.asSelf ?? true;

  // Cache-Control: no-cache prevents the browser from serving stale pet data
  // (same fix as referral endpoints - avoids 304 responses with old suika/stats)
  const response = await fetch(url.toString(), {
    headers: { 'Cache-Control': 'no-cache', ...(asSelf ? sessionAuthHeader() : {}) },
  });
  const data = await parseJson<FetchOwnerPetsResponse>(response);

  if (!data.success || !data.pets) {
    throw new Error(data.error || 'Failed to load pets');
  }

  return data.pets;
}

/**
 * Fetch a single pet (with its status) by pet id. Used by the convert-to-own promote
 * flow (Task 14) to load the free pet's current name/level/XP before payment.
 */
export async function fetchPetById(petId: string): Promise<PetWithStatus> {
  const response = await fetch(withBase(`/pets/${petId}`), {
    headers: { 'Cache-Control': 'no-cache', ...sessionAuthHeader() },
  });
  const data = await parseJson<{ success: boolean; pet?: PetWithStatus; error?: string }>(response);

  if (!data.success || !data.pet) {
    throw new Error(data.error || 'Failed to load pet');
  }

  return data.pet;
}

/**
 * Efficiently check if a user owns any pets (returns boolean only)
 * Much more efficient than fetchOwnerPets for quick ownership checks
 */
export async function checkUserHasAnyPets(ownerAddress: string): Promise<boolean> {
  const url = new URL(withBase('/pets/has-pets'));
  url.searchParams.set('owner', ownerAddress);

  const response = await fetch(url.toString(), { headers: { ...sessionAuthHeader() } });
  const data = await parseJson<{ success: boolean; hasPets: boolean }>(response);

  if (!data.success) {
    // On error, return false to avoid redirects
    return false;
  }

  return data.hasPets;
}

/**
 * Fetch the signed-in player's transaction history (adoptions + Suika top-ups).
 * Authenticated callers are resolved by account on the backend (all linked wallets);
 * `ownerAddress` is only used as a fallback for connected-but-unauthenticated Sui users.
 */
export async function fetchMyTransactions(ownerAddress?: string): Promise<MyTransactionDto[]> {
  const url = new URL(withBase('/payment/my-transactions'));
  if (ownerAddress) {
    url.searchParams.set('owner', ownerAddress);
  }

  const response = await fetch(url.toString(), {
    headers: { 'Cache-Control': 'no-cache', ...sessionAuthHeader() },
  });
  const data = await parseJson<MyTransactionsResponse>(response);

  if (!data.success || !data.transactions) {
    throw new Error(data.error || 'Failed to load transactions');
  }

  return data.transactions;
}

export async function requestPetTick(statusId: string): Promise<TickOutcome> {
  const response = await fetch(withBase('/actions/tick'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ statusId }),
  });

  return parseJson<TickOutcome>(response);
}

/**
 * Generic helper to execute pet actions with consistent error handling.
 * Reduces code duplication across all action functions.
 */
async function executePetAction<T = Record<string, never>>(
  endpoint: string,
  statusId: string,
  params?: T
): Promise<ActionOutcome> {
  const response = await fetch(withBase(endpoint), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ statusId, ...params }),
  });

  return parseJson<ActionOutcome>(response);
}

/**
 * Care-minigame result reported alongside an action (Fruit Catch on feed-meal,
 * Rhythm before the training wheel, Bubble Scrub on clean). The backend clamps
 * and tiers it server-side; the response's `minigame` field carries the grant.
 */
export type MinigameResult = import('@shared/domain/minigameConstants').MinigameResultPayload;

export async function feedPetBackend(
  statusId: string,
  foodType: number,
  minigame?: MinigameResult
): Promise<ActionOutcome> {
  return executePetAction('/actions/feed', statusId, { foodType, minigame });
}

export async function trainPetBackend(
  statusId: string,
  minigame?: MinigameResult
): Promise<
  ActionOutcome & {
    wheelSegmentIndex?: number;
    /** Rhythm-upgrade tier the wheel was rolled at ('none' = standard wheel). */
    wheelTier?: import('@shared/domain/minigameConstants').MinigameTier;
  }
> {
  return executePetAction('/actions/train', statusId, { minigame });
}

export async function refillEnergyBackend(statusId: string): Promise<ActionOutcome> {
  return executePetAction('/actions/refill', statusId);
}

export async function cleanPoopBackend(
  statusId: string,
  minigame?: MinigameResult
): Promise<ActionOutcome> {
  return executePetAction('/actions/clean', statusId, { minigame });
}

export async function partialCleanBackend(statusId: string): Promise<ActionOutcome> {
  return executePetAction('/actions/clean-partial', statusId);
}

export async function giveMedicineBackend(statusId: string): Promise<ActionOutcome> {
  return executePetAction('/actions/medicine', statusId);
}

export async function startRestBackend(statusId: string): Promise<ActionOutcome> {
  return executePetAction('/actions/rest/start', statusId);
}

export async function stopRestBackend(statusId: string): Promise<ActionOutcome> {
  return executePetAction('/actions/rest/stop', statusId);
}

export async function requestEvolutionToken(
  petId: string,
  ownerAddress: string,
  withShareBonus: boolean = false
): Promise<RequestEvolutionTokenResult> {
  const response = await fetch(withBase('/evolution/request-token'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ petId, ownerAddress, withShareBonus }),
  });

  return parseJson<RequestEvolutionTokenResult>(response);
}

/**
 * Sponsored Evolution API Types
 */
export interface PrepareSponsoredEvolutionResponse {
  success: boolean;
  transactionBytes?: string;
  sponsorAddress?: string;
  preview?: {
    petId: string;
    tokenId: string;
  };
  error?: string;
}

export interface ExecuteSponsoredEvolutionResponse {
  success: boolean;
  digest?: string;
  error?: string;
}

/**
 * Prepare a sponsored evolution transaction
 * Backend creates token internally (with cooldown protection) and builds unsigned transaction
 *
 * @param petId - Pet NFT object ID
 * @param userAddress - User wallet address (proves ownership)
 * @param withShareBonus - If true, grants XP bonus for sharing on social media
 */
/**
 * Backend-only evolution / hatch (Phase 1 custody). No user signature — the backend
 * signs use_evolution_token as the on-chain custody owner. `needsDeposit` is true when
 * the pet must be deposited into custody first (deposit-then-act).
 */
export interface ExecuteEvolutionResponse {
  success: boolean;
  digest?: string;
  tokenType?: 'stage' | 'level';
  breedingTokenCreated?: boolean;
  breedingTokenId?: string;
  needsDeposit?: boolean;
  error?: string;
  remainingXp?: number;
  requiredXp?: number;
  currentXp?: number;
  /** True when this pet's level/XP growth clamped at the free-play cap (FreeCapModal). */
  freeCapReached?: boolean;
}

export async function executeBackendEvolution(
  petId: string,
  userAddress: string,
  withShareBonus: boolean = false,
): Promise<ExecuteEvolutionResponse> {
  const response = await fetch(withBase('/evolution/execute'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ petId, userAddress, withShareBonus }),
  });

  return parseJson<ExecuteEvolutionResponse>(response);
}

export async function syncPetState(petId: string): Promise<SyncPetStateResult> {
  const response = await fetch(withBase('/evolution/sync-pet-state'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ petId }),
  });

  return parseJson<SyncPetStateResult>(response);
}

/**
 * Replenish Suika balance for a pet after the user's USDC transfer is confirmed.
 *
 * @param petId - Pet object ID to replenish
 * @param txDigest - Digest of the user's USDC transfer transaction
 * @param amountUsdc - Amount of USDC transferred
 */
export interface ReplenishSuikaResult {
  success: boolean;
  petId?: string;
  suikaAmount?: number;
  error?: string;
}

export interface FreeSuikaRefillResult {
  success: boolean;
  amount?: number;
  error?: string;
}

/**
 * Web2 (the game is free): refill a pet that is below SUIKA_LOW_THRESHOLD back up to
 * SUIKA_FREE_REFILL_TARGET, and wake it if it hibernated. No payment, no wallet.
 */
export async function refillSuikaFree(petId: string): Promise<FreeSuikaRefillResult> {
  const response = await fetch(withBase('/actions/refill-suika'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ statusId: petId }),
  });

  return parseJson<FreeSuikaRefillResult>(response);
}

export async function replenishSuika(
  petId: string,
  txDigest: string,
  amountUsdc: number,
  chain: PaymentChain = 'sui',
): Promise<ReplenishSuikaResult> {
  const response = await fetch(withBase('/payment/replenish-suika'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ petId, txDigest, amountSUI: amountUsdc, chain }), // wire field name unchanged; value is now USDC-denominated
  });

  return parseJson<ReplenishSuikaResult>(response);
}

/**
 * Buy Suika into the TRAINER's pooled balance after the player's USDC transfer is confirmed.
 *
 * Same purchase as `replenishSuika`, minus the pet: the Suika lands on the trainer, who can
 * move it onto any Suikamon later with `transferTrainerSuika`.
 *
 * @param walletAddress - The acting identity: the payer, and the balance credited
 * @param txDigest - Chain-native reference for the USDC transfer
 * @param amountUsdc - Amount of USDC transferred
 * @param chain - Chain the payment was made on
 */
export interface PurchaseTrainerSuikaResult {
  success: boolean;
  suikaAmount?: number;
  trainerBalanceAfter?: number;
  error?: string;
}

export async function purchaseTrainerSuika(
  walletAddress: string,
  txDigest: string,
  amountUsdc: number,
  chain: PaymentChain = 'sui',
): Promise<PurchaseTrainerSuikaResult> {
  const response = await fetch(withBase('/payment/purchase-trainer-suika'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ walletAddress, txDigest, amountUsdc, chain }),
  });

  return parseJson<PurchaseTrainerSuikaResult>(response);
}

export async function fetchSpeciesSupply(): Promise<SpeciesSupplyMetricDto[]> {
  const response = await fetch(withBase('/pets/species-supply'));
  const data = await parseJson<FetchSpeciesSupplyResponse>(response);
  if (!data.success || !data.supply) {
    throw new Error(data.error || 'Failed to load species supply');
  }
  return data.supply;
}

/**
 * Fetch a paginated set of pets with optional filters.
 * This module fetches summarized pet data from the backend for public viewing.
 */
export async function fetchBrowsePetsPage(request?: BrowsePetsRequest): Promise<BrowsePetsPageResult> {
  const filters = request?.filters;
  const params = new URLSearchParams();

  if (filters?.species !== undefined) {
    params.append('species', filters.species.toString());
  }
  if (filters?.minLevel !== undefined) {
    params.append('minLevel', filters.minLevel.toString());
  }
  if (filters?.maxLevel !== undefined) {
    params.append('maxLevel', filters.maxLevel.toString());
  }
  if (filters?.hibernated !== undefined) {
    params.append('hibernated', filters.hibernated.toString());
  }
  if (request?.limit !== undefined) {
    params.append('limit', request.limit.toString());
  }
  if (request?.cursor) {
    params.append('cursor', request.cursor);
  }

  const url = withBase(`/pets/browse${params.toString() ? `?${params.toString()}` : ''}`);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch browse pets: ${response.statusText}`);
  }

  const data: BrowsePetsResponse = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Unknown error fetching pets');
  }

  return {
    pets: data.pets,
    total: data.total,
    nextCursor: data.nextCursor ?? null,
    hasNextPage: Boolean(data.hasNextPage),
  };
}

/**
 * Check if a pet name is available (not already taken or protected)
 * Called before opening wallet to validate name
 * Returns error message with reason: 'protected' (reserved name) or 'taken' (already used)
 */
export async function checkPetNameAvailability(name: string): Promise<{ available: boolean; error?: string; reason?: 'protected' | 'taken' }> {
  try {
    const response = await fetch(withBase('/pets/check-name'), {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ name }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { available: false, error: data.error || 'Failed to check name availability', reason: data.reason };
    }

    if (!data.available) {
      return { available: false, error: data.error || 'Name is not available', reason: data.reason };
    }

    return { available: true };
  } catch (error) {
    console.error('Error checking pet name availability:', error);
    return { available: false, error: 'Network error checking name availability' };
  }
}

export interface PublicPetData {
  id: string;
  name: string;
  description: string;
  image_url: string;
  species: number;
  rarity: number;
  stage: number;
  level: number;
  birth_time: number;
  mint_number: number;
  owner: string;
}

export interface PetStatistics {
  feedCount: number;
  trainCount: number;
  medicineCount: number;
  cleanCount: number;
  totalActions: number;
  suikaBalance: number;
  battleStats: {
    totalBattles: number;
    wins: number;
    losses: number;
    draws: number;
    winRate: number;
  };
}

/**
 * Fetch public pet information (for sharing and public display)
 * This endpoint does not require authentication and excludes game stats
 */
/** Thrown for a web3-era Suikamon (before the web2 reset): history, not a live pet. */
export class LegacyPetError extends Error {
  constructor(message = 'This Suikamon is from before Suikamon 20') {
    super(message);
    this.name = 'LegacyPetError';
  }
}

export async function fetchPublicPetDetails(petId: string): Promise<PublicPetData> {
  const response = await fetch(withBase(`/pets/${petId}/public`));
  const data = await parseJson<{ success: boolean; pet: PublicPetData; error?: string; legacy?: boolean }>(response);

  if (data.legacy) throw new LegacyPetError(data.error);
  if (!data.success || !data.pet) {
    throw new Error(data.error || 'Failed to load pet details');
  }

  return data.pet;
}

/**
 * Fetch pet statistics (action counts and battle history)
 * This endpoint is for public display on pet profile pages
 */
export async function fetchPetStatistics(petId: string): Promise<PetStatistics> {
  const response = await fetch(withBase(`/pets/${petId}/stats`));
  const data = await parseJson<{ success: boolean; stats: PetStatistics; error?: string }>(response);

  if (!data.success || !data.stats) {
    throw new Error(data.error || 'Failed to load pet statistics');
  }

  return data.stats;
}

/**
 * Fetch community statistics for the home page
 * Shows aggregate game activity (trainers, pets, meals served, poops cleaned)
 */
export async function fetchCommunityStats(): Promise<CommunityStatsDto> {
  const response = await fetch(withBase('/pets/community-stats'));
  const data = await parseJson<FetchCommunityStatsResponse>(response);

  if (!data.success || !data.stats) {
    throw new Error(data.error || 'Failed to load community stats');
  }

  return data.stats;
}

/**
 * Fetch a paginated set of trainer rankings ordered by pet count.
 * This module fetches summarized trainer data from the backend for leaderboard viewing.
 */
export async function fetchBrowseTrainersPage(request?: BrowseTrainersRequest): Promise<BrowseTrainersPageResult> {
  const params = new URLSearchParams();

  if (request?.limit !== undefined) {
    params.append('limit', request.limit.toString());
  }
  if (request?.cursor) {
    params.append('cursor', request.cursor);
  }
  if (request?.search && request.search.trim() !== '') {
    params.append('search', request.search.trim());
  }

  const url = withBase(`/pets/trainers/rankings${params.toString() ? `?${params.toString()}` : ''}`);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch trainer rankings: ${response.statusText}`);
  }

  const data: BrowseTrainersResponse = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Unknown error fetching trainer rankings');
  }

  return {
    trainers: data.trainers,
    total: data.total,
    nextCursor: data.nextCursor ?? null,
    hasNextPage: Boolean(data.hasNextPage),
  };
}

// ===================================================================
// BREEDING API FUNCTIONS
// ===================================================================

export interface BreedingTokenStatus {
  success: boolean;
  eligible: boolean;
  nextMilestoneLevel?: number;
  message?: string;
  hasToken: boolean;
  tokenId?: string;
  milestoneLevel?: number;
}

export interface UseBreedingTokenResponse {
  success: boolean;
  childPetId?: string;
  childName?: string;
  childSpeciesId?: number;
  childSpeciesName?: string;
  childRarity?: string;
  mintTxDigest?: string;
  randomnessCheckpoint?: number;
  randomnessCheckpointDigest?: string;
  /** Set INSTEAD of childPetId when the egg rolled a species the player already owns — paid out as Suika. */
  suikaConverted?: number;
  newSuikaBalance?: number;
  message?: string;
  error?: string;
}

/**
 * Check if a pet has a breeding token available
 * Returns eligibility status and token availability
 *
 * @param petId - The pet's object ID
 */
export async function checkBreedingTokenStatus(petId: string): Promise<BreedingTokenStatus> {
  const url = new URL(withBase('/breeding/token-status'));
  url.searchParams.set('petId', petId);

  const response = await fetch(url.toString());
  const data = await parseJson<BreedingTokenStatus>(response);

  return data;
}

/**
 * Open a found egg BEFORE it is named: the species inside, or — when the player already owns that
 * species — the Suika it was paid out as (`suikaConverted`, nothing left to name). The roll is
 * stored server-side, so naming it with {@link createEggFromBreedingToken} hatches this species.
 */
export async function revealFoundEgg(
  parentPetId: string,
  ownerAddress: string
): Promise<UseBreedingTokenResponse> {
  const response = await fetch(withBase('/breeding/reveal-egg'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ parentPetId, ownerAddress }),
  });

  return parseJson<UseBreedingTokenResponse>(response);
}

/**
 * Create an egg from a breeding token (simplified flow)
 * Backend handles randomness server-side, no wallet signature needed
 *
 * @param parentPetId - The parent pet's object ID
 * @param eggName - Name for the new egg
 * @param ownerAddress - Owner's wallet address
 */
export async function createEggFromBreedingToken(
  parentPetId: string,
  eggName: string,
  ownerAddress: string
): Promise<UseBreedingTokenResponse> {
  const response = await fetch(withBase('/breeding/create-egg'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      parentPetId,
      eggName,
      ownerAddress,
    }),
  });

  return parseJson<UseBreedingTokenResponse>(response);
}

// ===================================================================
// REFERRAL API FUNCTIONS
// ===================================================================

/**
 * Response from the referral resolution endpoint
 */
export interface ReferralResolution {
  /** Whether the wallet is an eligible referral */
  eligible: boolean;
  /** Friendly name of the referrer (from backend config) */
  name?: string;
  /** Commission percentage for referrer (variable: 10%, 15%, 20%, 30%) */
  percent?: number;
  /** Discount percentage for buyer (always 10%) */
  userDiscount?: number;
}

/**
 * Resolve a referral wallet address
 * Checks if the wallet is an eligible referral partner
 *
 * @param wallet - The wallet address to check
 * @returns Referral eligibility status and details
 */
export async function resolveReferral(wallet: string): Promise<ReferralResolution> {
  const url = new URL(withBase('/referrals/resolve'));
  url.searchParams.set('wallet', wallet);

  // Add cache-busting to avoid 304 responses which break response.ok check
  const response = await fetch(url.toString(), {
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  // Handle both 200 and 304 (Not Modified) as success
  if (response.ok || response.status === 304) {
    const data = await response.json();
    return data as ReferralResolution;
  }

  // For actual errors, throw
  const data = await response.json().catch(() => ({}));
  const message = (data as { error?: string })?.error || response.statusText || 'Failed to resolve referral';
  throw new Error(message);
}

/**
 * Referral partner info with earnings
 */
export interface ReferralPartnerStats {
  /** Display name of the partner */
  name: string;
  /** Wallet address of the partner */
  wallet: string;
  /** Commission percentage for this partner */
  percent: number;
  /** Total SUI earned from referrals */
  totalEarnedSUI: number;
}

/**
 * Response from the referral stats endpoint
 */
export interface ReferralStatsResponse {
  success: boolean;
  /** User discount percentage (always 10%) */
  userDiscount: number;
  /** List of partners with their earnings */
  partners: ReferralPartnerStats[];
}

/**
 * Fetch referral partner statistics
 * Gets list of all referral partners with their total earnings
 *
 * @returns Referral stats with partner earnings
 */
export async function fetchReferralStats(): Promise<ReferralStatsResponse> {
  const response = await fetch(withBase('/referrals/stats'), {
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  return parseJson<ReferralStatsResponse>(response);
}

/**
 * Player-specific referral statistics
 */
export interface ReferralPlayerStats {
  success: boolean;
  wallet: string;
  /** Number of successful referrals */
  referralCount: number;
  /** Total SUI earned from referrals */
  totalEarnedSUI: number;
}

/**
 * Fetch referral stats for a specific wallet address.
 * Used on the ReferralsPage to show player's own referral performance.
 */
export async function fetchMyReferralStats(wallet: string): Promise<ReferralPlayerStats> {
  const url = new URL(withBase('/referrals/stats'));
  url.searchParams.set('wallet', wallet);

  const response = await fetch(url.toString(), {
    headers: {
      'Cache-Control': 'no-cache',
    },
  });

  return parseJson<ReferralPlayerStats>(response);
}

// ===================================================================
// AIRDROP API FUNCTIONS
// ===================================================================

export interface AirdropValidationResult {
  valid: boolean;
  speciesId?: number | null;
  expired?: boolean;
  reason?: string;
}

export interface AirdropRedeemResult {
  success: boolean;
  petId?: string;
  txDigest?: string;
  message?: string;
  error?: string;
}

export interface AirdropCodeData {
  code: string;
  species_id: number | null;
  redeemed: boolean;
  redeemed_by: string | null;
  redeemed_pet_id: string | null;
  redeemed_at: string | null;
  created_at: string;
  expires_at: string | null;
  note: string;
  max_uses: number;
  uses_count: number;
  new_trainers_only: boolean;
}

/**
 * Validate an airdrop code
 */
export async function validateAirdropCode(code: string, walletAddress: string): Promise<AirdropValidationResult> {
  const url = new URL(withBase('/airdrop/validate'));
  url.searchParams.set('code', code);
  url.searchParams.set('wallet', walletAddress);

  const response = await fetch(url.toString());
  return parseJson<AirdropValidationResult>(response);
}

/**
 * Redeem an airdrop code for a free mint
 */
export async function redeemAirdropCode(
  code: string,
  species: number,
  name: string,
  userAddress: string,
): Promise<AirdropRedeemResult> {
  const response = await fetch(withBase('/airdrop/redeem'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ code, species, name, userAddress }),
  });

  return parseJson<AirdropRedeemResult>(response);
}

/**
 * Create airdrop codes (admin only)
 */
export async function createAirdropCodes(
  params: {
    speciesId?: number | null;
    note?: string;
    expiresAt?: string | null;
    count?: number;
    maxUses?: number;
    newTrainersOnly?: boolean;
  },
  _walletAddress: string,
): Promise<{ success: boolean; codes: AirdropCodeData[] }> {
  const response = await fetch(withBase('/airdrop/codes'), {
    method: 'POST',
    headers: adminSessionHeaders(),
    body: JSON.stringify(params),
  });

  return parseJson<{ success: boolean; codes: AirdropCodeData[] }>(response);
}

// ===================================================================
// TELEGRAM LINKING API FUNCTIONS
// ===================================================================

export interface GenerateLinkTokenResult {
  success: boolean;
  token: string;
  expiresInMinutes: number;
}

export interface TelegramLinkStatus {
  success: boolean;
  linked: boolean;
  telegramUsername: string | null;
  linkedPetId: string | null;
}

/**
 * Generate a short-lived link token for the current wallet and an optional pet.
 * The user copies the token and sends /link <TOKEN> to the Telegram bot.
 * Passing petId stores which Suikamon is linked, enabling future Telegram interactions.
 */
export async function generateTelegramLinkToken(walletAddress: string, petId?: string): Promise<GenerateLinkTokenResult> {
  const response = await fetch(withBase('/telegram/generate-link-token'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ walletAddress, petId }),
  });
  return parseJson<GenerateLinkTokenResult>(response);
}

/**
 * Check whether a wallet is already linked to a Telegram account.
 */
export async function fetchTelegramLinkStatus(walletAddress: string): Promise<TelegramLinkStatus> {
  const url = new URL(withBase('/telegram/link-status'));
  url.searchParams.set('wallet', walletAddress);
  const response = await fetch(url.toString());
  return parseJson<TelegramLinkStatus>(response);
}

/**
 * Switch which Suikamon is linked to the Telegram account.
 * The wallet must already be linked — this just updates the pet.
 */
export async function switchTelegramPet(walletAddress: string, petId: string): Promise<{ success: boolean }> {
  const response = await fetch(withBase('/telegram/switch-pet'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ walletAddress, petId }),
  });
  return parseJson<{ success: boolean }>(response);
}

/**
 * Unlink the wallet from its Telegram account.
 */
export async function unlinkTelegram(walletAddress: string): Promise<{ success: boolean; unlinked: boolean }> {
  const response = await fetch(withBase('/telegram/unlink'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ walletAddress }),
  });
  return parseJson<{ success: boolean; unlinked: boolean }>(response);
}

/**
 * List all airdrop codes (admin only)
 */
export async function listAirdropCodes(
  _walletAddress: string,
): Promise<{ success: boolean; codes: AirdropCodeData[] }> {
  const response = await fetch(withBase('/airdrop/codes'), {
    headers: {
      ...adminSessionHeaders(),
      'Cache-Control': 'no-cache',
    },
  });

  return parseJson<{ success: boolean; codes: AirdropCodeData[] }>(response);
}

// ============================================================================
// Trainer Profile
// ============================================================================

/**
 * Fetch a trainer's profile by wallet address.
 * Returns null profile if none exists.
 */
export async function fetchTrainerProfile(walletAddress: string): Promise<TrainerProfile | null> {
  const url = new URL(withBase('/trainers/profile'));
  url.searchParams.set('wallet', walletAddress);
  const response = await fetch(url.toString());
  const result = await parseJson<TrainerProfileResponse>(response);
  return result.profile;
}

/**
 * Create or update the signed-in player's trainer profile. The session decides whose profile
 * it is, so the request carries it. Throws with the server's reason when the save is refused.
 */
export async function saveTrainerProfile(data: SaveTrainerProfileRequest): Promise<TrainerProfile | null> {
  const response = await fetch(withBase('/trainers/profile'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify(data),
  });
  const result = await parseJson<TrainerProfileResponse & { error?: string }>(response);
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Failed to save profile. Please try again.');
  }
  return result.profile;
}

/**
 * Prepare a sponsored egg donation transaction.
 * Backend builds the transfer tx with sponsor as gas payer.
 */
export interface PrepareSponsoredDonationResponse {
  success: boolean;
  transactionBytes?: string;
  sponsorAddress?: string;
  error?: string;
}

/**
 * DB-only egg donation / hatched-pet sale (Phase 1 custody). No on-chain transfer, no
 * signature. `needsDeposit` is true when the pet must be deposited into custody first.
 */
export interface TrainerRewardResponse {
  success: boolean;
  suikaRewarded?: number;
  newTrainerBalance?: number;
  needsDeposit?: boolean;
  error?: string;
}

export async function donateEgg(
  walletAddress: string,
  petId: string,
): Promise<TrainerRewardResponse> {
  const response = await fetch(withBase('/trainers/donate-egg'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ walletAddress, petId }),
  });
  return parseJson<TrainerRewardResponse>(response);
}

export async function sellHatchedPet(
  walletAddress: string,
  petId: string,
): Promise<TrainerRewardResponse> {
  const response = await fetch(withBase('/trainers/sell-pet'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ walletAddress, petId }),
  });
  return parseJson<TrainerRewardResponse>(response);
}

// ─── Custody: deposit (sponsored) + withdraw (SIWS) ──────────────────────────

export interface PrepareCustodyDepositResponse {
  success: boolean;
  transactionBytes?: string;
  sponsorAddress?: string;
  error?: string;
}

export async function prepareCustodyDeposit(
  petId: string,
  userAddress: string,
): Promise<PrepareCustodyDepositResponse> {
  const response = await fetch(withBase('/custody/deposit/prepare'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ petId, userAddress }),
  });
  return parseJson<PrepareCustodyDepositResponse>(response);
}

export interface ExecuteCustodyDepositResponse {
  success: boolean;
  digest?: string;
  suikaRewarded?: number;
  newSuikaBalance?: number;
  error?: string;
}

export async function executeCustodyDeposit(
  signedTxBytes: string,
  userSignature: string,
  userAddress: string,
): Promise<ExecuteCustodyDepositResponse> {
  const response = await fetch(withBase('/custody/deposit/execute'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ signedTxBytes, userSignature, userAddress }),
  });
  return parseJson<ExecuteCustodyDepositResponse>(response);
}

export async function getWithdrawChallenge(): Promise<{ success: boolean; nonce?: string; error?: string }> {
  const response = await fetch(withBase('/custody/withdraw-challenge'), { method: 'GET' });
  return parseJson<{ success: boolean; nonce?: string; error?: string }>(response);
}

export interface WithdrawResponse {
  success: boolean;
  digest?: string;
  recipient?: string;
  error?: string;
}

export async function withdrawPet(
  petId: string,
  wallet: string,
  nonce: string,
  signature: string,
): Promise<WithdrawResponse> {
  const response = await fetch(withBase('/custody/withdraw'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ petId, wallet, nonce, signature }),
  });
  return parseJson<WithdrawResponse>(response);
}

/**
 * Backend-only rename (Phase 1 custody). No user signature — the backend signs
 * rename_suikamon as the on-chain custody owner. Requires the pet to be `in_custody`;
 * `needsDeposit` is true when the pet must be deposited into custody first.
 */
export interface RenamePetResponse {
  success: boolean;
  newName?: string;
  digest?: string;
  needsDeposit?: boolean;
  error?: string;
}

export async function renamePet(
  petId: string,
  userAddress: string,
  newName: string,
): Promise<RenamePetResponse> {
  const response = await fetch(withBase('/pets/rename'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ petId, userAddress, newName }),
  });
  return parseJson<RenamePetResponse>(response);
}

export interface ReleasePetResponse {
  success: boolean;
  error?: string;
}

/**
 * "Delete" a Suikamon the player owns. The backend keeps the row, ownerless and out of the game
 * (petReleaseService.ts); `confirmName` is what the player typed and must be the pet's name.
 */
export async function releasePet(
  petId: string,
  userAddress: string,
  confirmName: string,
): Promise<ReleasePetResponse> {
  const response = await fetch(withBase('/pets/release'), {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...sessionAuthHeader() },
    body: JSON.stringify({ petId, userAddress, confirmName }),
  });
  return parseJson<ReleasePetResponse>(response);
}

export interface RecordPetTransferResponse {
  success: boolean;
  newOwner?: string;
  error?: string;
}

/**
 * Record a player-signed wallet→wallet transfer of a Suikamon held in the player's own wallet.
 * Call this AFTER the player has signed and executed the on-chain transferObjects — the backend
 * confirms the NFT landed with the recipient before updating the off-chain owner of record.
 */
export async function recordPetTransfer(
  petId: string,
  fromAddress: string,
  toAddress: string,
): Promise<RecordPetTransferResponse> {
  const response = await fetch(withBase('/pets/transfer'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ petId, fromAddress, toAddress }),
  });
  return parseJson<RecordPetTransferResponse>(response);
}

/**
 * Transfer SUIKA from trainer balance to a pet.
 * Amount must be a positive multiple of 100.
 */
export async function transferTrainerSuika(
  walletAddress: string,
  petId: string,
  amount: number,
): Promise<TransferSuikaResponse> {
  const response = await fetch(withBase('/trainers/transfer-suika'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ walletAddress, petId, amount }),
  });
  return parseJson<TransferSuikaResponse>(response);
}

// ============================================================
// Seasons / Ranked Leaderboard (US6)
// ============================================================

export interface SeasonLeaderboardEntry {
  rank: number;
  pet_id: string;
  pet_name: string;
  species_id: number;
  level: number;
  trophies: number;
  wins: number;
  losses: number;
  draws: number;
  owner_address: string | null;
}

export interface SeasonLeaderboardResponse {
  success: boolean;
  season: string;
  source: 'live' | 'snapshot';
  entries: SeasonLeaderboardEntry[];
}

export interface SeasonListResponse {
  success: boolean;
  current: string;
  snapshotted: string[];
}

export async function fetchCurrentRankedSeason(): Promise<string> {
  const response = await fetch(withBase('/seasons/current'));
  const data = await parseJson<{ success: boolean; season: string }>(response);
  return data.season;
}

export async function fetchSeasonList(): Promise<SeasonListResponse> {
  const response = await fetch(withBase('/seasons/list'));
  return parseJson<SeasonListResponse>(response);
}

export async function fetchSeasonLeaderboard(
  seasonLabel: string,
  limit = 10,
): Promise<SeasonLeaderboardResponse> {
  const url = withBase(`/seasons/${encodeURIComponent(seasonLabel)}/leaderboard?limit=${limit}`);
  const response = await fetch(url);
  return parseJson<SeasonLeaderboardResponse>(response);
}
